import { inject, Injectable } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ENVIRONMENTS } from '../enums/environments.enum';
import { timeout } from 'rxjs/internal/operators/timeout';
import { SessionStorageService } from './app-session.service';

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private readonly session = inject(SessionStorageService);
  private readonly calService = inject(CalAngularService);

  private tokenRequestInFlight: Promise<boolean> | null = null;
  getAuthorizationToken(): string | null {
    return this.session.getItem('accessToken');
  }

  isTokenExpired(): boolean {
    const token = this.getAuthorizationToken();
    if (!token) {
      return true;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return false;
    }

    try {
      const encodedPayload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = encodedPayload.padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
      const payload = JSON.parse(atob(paddedPayload));
      const exp = payload.exp;
      if (!exp) {
        return true;
      }
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  }

  getUserImpersonationToken(): Promise<boolean> {
    this.tokenRequestInFlight ??= this.requestUserImpersonationToken().finally(() => {
      this.tokenRequestInFlight = null;
    });
    return this.tokenRequestInFlight;
  }

  private async requestUserImpersonationToken(): Promise<boolean> {
    const envUrl = environment.production ? ENVIRONMENTS.PROD : ENVIRONMENTS.DEV;
    const scope = [`${envUrl}/user_impersonation`];
    try {
      const token = await firstValueFrom(
        this.calService.getAADToken(scope).pipe(timeout(environment.requestTimeout)),
      );
      if (!token) {
        return false;
      }
      this.session.setItem('accessToken', token);
      return true;
    } catch (error) {
      console.error('Error while fetching user impersonation token:', error);
      throw error;
    }
  }

  async refreshToken(): Promise<void> {
    await this.getUserImpersonationToken();
  }
}
